/**
 * The dashboard's implementation of the shared {@link ScopePrefsStore}.
 *
 * Both tenancy axes have a user-editable default per resource, and this app keeps
 * them in the server-backed {@link useDashboardPrefs} blob (a `saved-views-api`
 * `user` row, cached locally in `ScopedStorage`). What a stored value MEANS —
 * whether it still names a tier this resource offers, and what to fall back to
 * when it does not — is `@authe/fe-commons/attribution`, so a rollout that
 * narrows the offered rungs cannot leave a stale preference being written at.
 *
 * That is the whole reason this is an adapter rather than two hooks with their own
 * resolution: the admin portal has different storage and must not have different
 * rules. `ui-core`'s scope hints and preferences panel are written against the
 * interface below, not against this file.
 *
 * The Transactions feed default lives beside it rather than in it: it is a
 * tenancy default the user sets in the same breath, but it is not a resolve tier
 * on this ladder — the explorer owns its own scope vocabulary.
 */
import * as React from "react";
import {
  effectiveScopes,
  type AttributionReadScope,
  type AttributionResource,
  type AttributionScope,
  type ScopePrefsStore,
} from "@authe/fe-commons/attribution";
import { OVERLAY_CONFIG } from "@/config";
import { DEFAULT_TX_SCOPE_ID, type TxScopeId } from "@/features/transactions/scopes";
import { useDashboardPrefs } from "./dashboardPrefs";

export interface ScopePrefs extends ScopePrefsStore {
  /** Effective write tier for `resource`: a valid override, else this app's default. */
  resolveDefaultWriteScope: (resource: AttributionResource) => AttributionScope;
  /** Effective resolve tier for `resource`. */
  resolveDefaultResolveScope: (resource: AttributionResource) => AttributionReadScope;
  /** True when the caller has deliberately overridden the product default. */
  isWriteOverridden: (resource: AttributionResource) => boolean;
  isResolveOverridden: (resource: AttributionResource) => boolean;

  /** The saved Transactions feed default, or `null` when using the product default. */
  txFeedScope: TxScopeId | null;
  /** Effective Transactions feed default: override (if valid) else product default. */
  resolveDefaultTxFeedScope: () => TxScopeId;
  /** Persist the Transactions feed default; pass `null` to clear it. */
  setDefaultTxFeedScope: (scope: TxScopeId | null) => void;
}

/** The reactive per-resource tenancy defaults, resolved against this app's policy. */
export function useScopePrefs(): ScopePrefs {
  const { blob, setWriteScope, setResolveScope, setTxFeed } = useDashboardPrefs();
  const { writeScope, resolveScope, txFeed } = blob;

  return React.useMemo<ScopePrefs>(() => {
    const store: ScopePrefsStore = { writeScope, resolveScope, setWriteScope, setResolveScope };
    const effective = effectiveScopes(store, OVERLAY_CONFIG);
    return {
      ...store,
      resolveDefaultWriteScope: effective.writeScope,
      resolveDefaultResolveScope: effective.readScope,
      isWriteOverridden: effective.isWriteOverridden,
      isResolveOverridden: effective.isReadOverridden,
      txFeedScope: txFeed,
      resolveDefaultTxFeedScope: () => txFeed ?? DEFAULT_TX_SCOPE_ID,
      setDefaultTxFeedScope: setTxFeed,
    };
  }, [resolveScope, setResolveScope, setTxFeed, setWriteScope, txFeed, writeScope]);
}
